import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import Papa, { ParseError, ParseResult } from "papaparse";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { PageContainer } from "../../components/PageContainer";
import { GET_BANDS } from "../../components/BandPicker/graphql";
import { GET_ALL_STAGES } from "../stages/graphql";
import { CREATE_STAGE } from "../stage/graphql";
import { CREATE_BAND } from "../band/graphql";
import { CREATE_BOOKING, DELETE_BOOKING } from "../booking/graphql";
import { GET_EDITIONS } from "../editions/graphql";
import {
  CREATE_UPDATE_ANNOUNCEMENT,
  DELETE_ANNOUNCEMENT,
  GET_ANNOUNCEMENTS,
} from "../announcement/graphql";

const HEADER_ALIASES = {
  artist: ["confirmed artist", "artist", "band"],
  venue: ["venue", "stage"],
  date: ["date"],
  doorTime: ["door time", "door"],
  startTime: ["start time", "start"],
  endTime: ["end time", "end"],
  copy: ["copy", "description"],
} as const;

type HeaderKey = keyof typeof HEADER_ALIASES;

const REQUIRED_HEADERS: HeaderKey[] = ["artist", "venue", "startTime", "endTime"];

const HEADER_LABELS: Record<HeaderKey, string> = {
  artist: "Artist (artist/band)",
  venue: "Stage/Venue",
  date: "Date",
  doorTime: "Door Time",
  startTime: "Start Time",
  endTime: "End Time",
  copy: "Description",
};

const PREVIEW_HEADERS = [
  "Confirmed Artist",
  "Venue",
  "Date",
  "Start Time",
  "End Time",
];

type CsvBookingRow = {
  artistName: string;
  venueName: string;
  dateRaw: string;
  doorTimeRaw: string;
  startTimeRaw: string;
  endTimeRaw: string;
  copyRaw: string;
};

type DateParts = {
  year: number;
  monthIndex: number;
  day: number;
};

type TimeParts = {
  hour: number;
  minute: number;
};

type RowSubmissionState = "idle" | "loading" | "success" | "error";

type MatchStatus = "ready" | "missing" | "duplicate";

type NameMatch = {
  id: string;
  name: string;
};

type RowDetails = {
  row: CsvBookingRow;
  bandKey: string;
  stageKey: string;
  bandStatus: MatchStatus;
  stageStatus: MatchStatus;
  bandMatches: NameMatch[];
  stageMatches: NameMatch[];
  issues: string[];
  warnings: string[];
  dateParts?: DateParts;
  startTimeParts?: TimeParts;
  endTimeParts?: TimeParts;
  startDateTime?: Date;
  endDateTime?: Date;
};

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

const DAY_MS = 24 * 60 * 60 * 1000;

const stripBom = (value: string) => value.replace(/^\uFEFF/, "");

const sanitiseCell = (value: string | null | undefined) =>
  stripBom((value ?? "").toString()).trim();

const normaliseHeader = (value: string) => sanitiseCell(value).toLowerCase();

const normaliseName = (value: string) => sanitiseCell(value).toLowerCase();

const stripWrappingQuotes = (value: string) => {
  if (value.length >= 2 && value.startsWith("\"") && value.endsWith("\"")) {
    return value.slice(1, -1);
  }
  return value;
};

const resolveHeaderMap = (headers: string[]) => {
  const headerIndex: Partial<Record<HeaderKey, number>> = {};
  const warnings: string[] = [];

  headers.forEach((header, index) => {
    const normalised = normaliseHeader(header);
    const matchedKey = (Object.keys(HEADER_ALIASES) as HeaderKey[]).find(
      (key) => HEADER_ALIASES[key].some((alias) => alias === normalised)
    );

    if (!matchedKey) {
      return;
    }

    if (headerIndex[matchedKey] !== undefined) {
      warnings.push(
        `Duplicate header detected for ${HEADER_LABELS[matchedKey]} (using the first occurrence).`
      );
      return;
    }

    headerIndex[matchedKey] = index;
  });

  const missingRequired = REQUIRED_HEADERS.filter(
    (key) => headerIndex[key] === undefined
  );

  return { headerIndex, warnings, missingRequired };
};

const buildRows = (
  dataRows: string[][],
  headerIndex: Partial<Record<HeaderKey, number>>
) => {
  const rows: CsvBookingRow[] = [];
  const warnings: string[] = [];

  dataRows.forEach((row, index) => {
    if (!Array.isArray(row)) {
      warnings.push(`Row ${index + 2} is invalid and has been skipped.`);
      return;
    }

    const getCell = (key: HeaderKey) => {
      const columnIndex = headerIndex[key];
      if (columnIndex === undefined) {
        return "";
      }
      return sanitiseCell(row[columnIndex]);
    };

    const trimmedRow = [
      getCell("artist"),
      getCell("venue"),
      getCell("date"),
      getCell("doorTime"),
      getCell("startTime"),
      getCell("endTime"),
      getCell("copy"),
    ];

    if (trimmedRow.every((cell) => cell.length === 0)) {
      return;
    }

    const [
      artistName,
      venueName,
      dateRaw,
      doorTimeRaw,
      startTimeRaw,
      endTimeRaw,
      copyRaw,
    ] = trimmedRow;
    const copyClean = stripWrappingQuotes(copyRaw);

    if (!artistName || !venueName || !startTimeRaw || !endTimeRaw) {
      const missing = [] as string[];
      if (!artistName) missing.push("Confirmed Artist");
      if (!venueName) missing.push("Venue");
      if (!startTimeRaw) missing.push("Start Time");
      if (!endTimeRaw) missing.push("End Time");

      warnings.push(
        `Row ${index + 2} is missing values for ${missing.join(", ")}.`
      );
    }

    rows.push({
      artistName,
      venueName,
      dateRaw,
      doorTimeRaw,
      startTimeRaw,
      endTimeRaw,
      copyRaw: copyClean,
    });
  });

  return { rows, warnings };
};

const parseDateParts = (value: string, year: number | null) => {
  if (!value || year === null || Number.isNaN(year)) {
    return null;
  }

  const cleaned = sanitiseCell(value);
  if (!cleaned) return null;

  const match = cleaned.match(/(\d{1,2})(st|nd|rd|th)?\s+([A-Za-z]+)/i);
  if (!match) return null;

  const day = Number.parseInt(match[1], 10);
  if (!Number.isFinite(day) || day < 1 || day > 31) {
    return null;
  }

  const monthToken = match[3].toLowerCase();
  const monthKey = monthToken.slice(0, 3);
  const monthIndex = MONTH_INDEX[monthKey];

  if (monthIndex === undefined) {
    return null;
  }

  return { year, monthIndex, day };
};

const parseTimeParts = (value: string) => {
  const cleaned = sanitiseCell(value).toLowerCase().replace(/\s+/g, "");
  if (!cleaned) return null;

  const match = cleaned.match(/^(\d{1,2})(?:(?::|\.)(\d{2}))?(am|pm)$/i);
  if (!match) return null;

  let hour = Number.parseInt(match[1], 10);
  const minute = match[2] ? Number.parseInt(match[2], 10) : 0;

  if (!Number.isFinite(hour) || hour < 1 || hour > 12) {
    return null;
  }

  if (!Number.isFinite(minute) || minute < 0 || minute > 59) {
    return null;
  }

  const suffix = match[3].toLowerCase();

  if (hour === 12) {
    hour = suffix === "am" ? 0 : 12;
  } else if (suffix === "pm") {
    hour += 12;
  }

  return { hour, minute };
};

const stripTimezoneSuffix = (value: string) =>
  value.replace(/(z|[+-]\d{2}:?\d{2})$/i, "").trim();

const parseExplicitUtcDateTime = (value: string) => {
  const cleaned = stripTimezoneSuffix(sanitiseCell(value)).replace(/\.\d+$/, "");
  if (!cleaned) return null;

  const yearFirst =
    cleaned.match(
      /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})[ T]+(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?$/i
    ) ?? null;
  const dayFirst =
    cleaned.match(
      /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})[ T]+(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?\s*(am|pm)?$/i
    ) ?? null;

  const match = yearFirst || dayFirst;
  if (!match) return null;

  let year = 0;
  let month = 0;
  let day = 0;
  let hour = 0;
  let minute = 0;
  let second = 0;
  let meridiem: string | null = null;

  if (yearFirst) {
    year = Number.parseInt(match[1], 10);
    month = Number.parseInt(match[2], 10);
    day = Number.parseInt(match[3], 10);
    hour = Number.parseInt(match[4], 10);
    minute = Number.parseInt(match[5] ?? "0", 10);
    second = Number.parseInt(match[6] ?? "0", 10);
    meridiem = match[7] ?? null;
  } else {
    day = Number.parseInt(match[1], 10);
    month = Number.parseInt(match[2], 10);
    year = Number.parseInt(match[3], 10);
    hour = Number.parseInt(match[4], 10);
    minute = Number.parseInt(match[5] ?? "0", 10);
    second = Number.parseInt(match[6] ?? "0", 10);
    meridiem = match[7] ?? null;
  }

  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    !Number.isFinite(day) ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute) ||
    !Number.isFinite(second)
  ) {
    return null;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return null;
  }

  if (meridiem) {
    const lower = meridiem.toLowerCase();
    if (hour < 1 || hour > 12) return null;
    if (hour === 12) {
      hour = lower === "am" ? 0 : 12;
    } else if (lower === "pm") {
      hour += 12;
    }
  } else if (hour < 0 || hour > 23) {
    return null;
  }

  if (minute < 0 || minute > 59 || second < 0 || second > 59) {
    return null;
  }

  return new Date(Date.UTC(year, month - 1, day, hour, minute, second));
};

const datePartsFromUtcDate = (date: Date): DateParts => ({
  year: date.getUTCFullYear(),
  monthIndex: date.getUTCMonth(),
  day: date.getUTCDate(),
});

const buildUtcDate = (dateParts: DateParts) =>
  new Date(Date.UTC(dateParts.year, dateParts.monthIndex, dateParts.day));

const buildUtcDateTime = (dateParts: DateParts, timeParts: TimeParts) =>
  new Date(
    Date.UTC(
      dateParts.year,
      dateParts.monthIndex,
      dateParts.day,
      timeParts.hour,
      timeParts.minute
    )
  );

const buildNameMatchMap = (items: NameMatch[]) => {
  const map = new Map<string, NameMatch[]>();

  items.forEach((item) => {
    const key = normaliseName(item.name);
    if (!key) return;

    const list = map.get(key) ?? [];
    list.push(item);
    map.set(key, list);
  });

  return map;
};

const getMatchStatus = (matches: NameMatch[]) => {
  if (matches.length === 0) return "missing";
  if (matches.length > 1) return "duplicate";
  return "ready";
};

const renderStatusIndicator = (
  status?: RowSubmissionState,
  errorMessage?: string
) => {
  if (status === "loading") {
    return <CircularProgress size={18} />;
  }

  if (status === "success") {
    return <CheckCircleOutlineIcon color="success" fontSize="small" />;
  }

  if (status === "error") {
    const icon = <HighlightOffIcon color="error" fontSize="small" />;
    return errorMessage ? <Tooltip title={errorMessage}>{icon}</Tooltip> : icon;
  }

  return null;
};

const renderLookupStatus = (status: MatchStatus, message?: string) => {
  if (status === "ready") {
    const icon = <CheckCircleOutlineIcon color="success" fontSize="small" />;
    return message ? <Tooltip title={message}>{icon}</Tooltip> : icon;
  }

  const icon = <HighlightOffIcon color="error" fontSize="small" />;
  return message ? <Tooltip title={message}>{icon}</Tooltip> : icon;
};

export const BookingCsvImportContent = () => {
  const [fileName, setFileName] = useState<string>();
  const [rows, setRows] = useState<CsvBookingRow[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);
  const [headerPreview, setHeaderPreview] = useState<string[]>([]);
  const [rowWarnings, setRowWarnings] = useState<string[]>([]);
  const [rawRowCount, setRawRowCount] = useState<number>(0);
  const [selectedEditionId, setSelectedEditionId] = useState<string>("");
  const [bandCreateStatuses, setBandCreateStatuses] = useState<
    Record<string, RowSubmissionState>
  >({});
  const [bandCreateErrors, setBandCreateErrors] = useState<Record<string, string>>(
    {}
  );
  const [stageCreateStatuses, setStageCreateStatuses] = useState<
    Record<string, RowSubmissionState>
  >({});
  const [stageCreateErrors, setStageCreateErrors] = useState<
    Record<string, string>
  >({});
  const [bookingStatuses, setBookingStatuses] = useState<
    Record<number, RowSubmissionState>
  >({});
  const [bookingErrors, setBookingErrors] = useState<Record<number, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeletingAnnouncements, setIsDeletingAnnouncements] = useState(false);
  const [submissionCounts, setSubmissionCounts] = useState<{
    success: number;
    failure: number;
  } | null>(null);
  const [createdBands, setCreatedBands] = useState<NameMatch[]>([]);
  const [createdStages, setCreatedStages] = useState<NameMatch[]>([]);
  const [importAnnouncementId, setImportAnnouncementId] = useState<string | null>(
    null
  );

  const organisationId = process.env.REACT_APP_ORG_ID ?? "";

  const { data: bandsData, loading: bandsLoading } = useQuery(GET_BANDS);
  const { data: stagesData, loading: stagesLoading } = useQuery(GET_ALL_STAGES);
  const { data: editionsData, loading: editionsLoading } =
    useQuery(GET_EDITIONS);
  const {
    data: announcementsData,
    loading: announcementsLoading,
    refetch: refetchAnnouncements,
  } = useQuery(GET_ANNOUNCEMENTS, {
    skip: !selectedEditionId,
    variables: {
      editionId: selectedEditionId,
    },
  });

  const [createStageMutation] = useMutation(CREATE_STAGE);
  const [createBandMutation] = useMutation(CREATE_BAND);
  const [createBookingMutation] = useMutation(CREATE_BOOKING);
  const [createAnnouncementMutation] = useMutation(CREATE_UPDATE_ANNOUNCEMENT);
  const [deleteBookingMutation] = useMutation(DELETE_BOOKING);
  const [deleteAnnouncementMutation] = useMutation(DELETE_ANNOUNCEMENT);

  const editions = editionsData?.festivalEditions ?? [];
  const selectedEdition = editions.find((edition) => edition.id === selectedEditionId);
  const editionYear = selectedEdition ? Number.parseInt(selectedEdition.year ?? "", 10) : null;

  useEffect(() => {
    if (!selectedEditionId && editions.length === 1) {
      setSelectedEditionId(editions[0].id);
    }
  }, [editions, selectedEditionId]);

  useEffect(() => {
    setImportAnnouncementId(null);
  }, [selectedEditionId]);

  useEffect(() => {
    setBookingStatuses({});
    setBookingErrors({});
    setSubmissionCounts(null);
    setIsSubmitting(false);
  }, [rows]);

  const bandOptions = useMemo(() => {
    const fetched = (bandsData?.bands ?? []).map((band) => ({
      id: band.id,
      name: band.name,
    }));
    return [...fetched, ...createdBands];
  }, [bandsData, createdBands]);

  const stageOptions = useMemo(() => {
    const fetched = (stagesData?.stages ?? []).map((stage) => ({
      id: stage.id,
      name: stage.name,
    }));
    return [...fetched, ...createdStages];
  }, [stagesData, createdStages]);

  const bandMatchMap = useMemo(() => buildNameMatchMap(bandOptions), [bandOptions]);
  const stageMatchMap = useMemo(
    () => buildNameMatchMap(stageOptions),
    [stageOptions]
  );

  const rowDetails: RowDetails[] = useMemo(() => {
    return rows.map((row) => {
      const bandKey = normaliseName(row.artistName);
      const stageKey = normaliseName(row.venueName);
      const bandMatches = bandMatchMap.get(bandKey) ?? [];
      const stageMatches = stageMatchMap.get(stageKey) ?? [];
      const bandStatus = getMatchStatus(bandMatches);
      const stageStatus = getMatchStatus(stageMatches);
      const issues: string[] = [];
      const warnings: string[] = [];

      if (!row.artistName) {
        issues.push("Missing artist name");
      } else if (bandStatus === "missing") {
        issues.push("Band not found");
      } else if (bandStatus === "duplicate") {
        issues.push("Multiple bands match this name");
      }

      if (!row.venueName) {
        issues.push("Missing venue name");
      } else if (stageStatus === "missing") {
        issues.push("Stage not found");
      } else if (stageStatus === "duplicate") {
        issues.push("Multiple stages match this name");
      }

      let startDateTime = parseExplicitUtcDateTime(row.startTimeRaw) ?? undefined;
      let endDateTime = parseExplicitUtcDateTime(row.endTimeRaw) ?? undefined;
      const startFromFull = Boolean(startDateTime);
      const endFromFull = Boolean(endDateTime);

      let datePartsFromColumn: DateParts | null = null;
      const needsDateColumn = !startFromFull && !endFromFull;

      if (row.dateRaw && needsDateColumn) {
        if (!selectedEditionId) {
          issues.push("Select an edition to validate dates");
        } else {
          datePartsFromColumn = parseDateParts(row.dateRaw, editionYear);
          if (!datePartsFromColumn) {
            issues.push("Invalid date");
          }
        }
      }

      let dateParts: DateParts | null =
        datePartsFromColumn ??
        (startDateTime ? datePartsFromUtcDate(startDateTime) : null) ??
        (endDateTime ? datePartsFromUtcDate(endDateTime) : null);

      let startTimeParts: TimeParts | null = null;
      if (!startFromFull) {
        if (!row.startTimeRaw) {
          issues.push("Missing start time");
        } else {
          startTimeParts = parseTimeParts(row.startTimeRaw);
          if (!startTimeParts) {
            issues.push("Invalid start time");
          }
        }
      }

      let endTimeParts: TimeParts | null = null;
      if (!endFromFull) {
        if (!row.endTimeRaw) {
          issues.push("Missing end time");
        } else {
          endTimeParts = parseTimeParts(row.endTimeRaw);
          if (!endTimeParts) {
            issues.push("Invalid end time");
          }
        }
      }

      if (!dateParts && (startTimeParts || endTimeParts)) {
        if (!row.dateRaw && needsDateColumn) {
          issues.push("Missing date");
        }
      }

      if (!startDateTime && dateParts && startTimeParts) {
        startDateTime = buildUtcDateTime(dateParts, startTimeParts);
      }

      if (!endDateTime && dateParts && endTimeParts) {
        endDateTime = buildUtcDateTime(dateParts, endTimeParts);
      }

      if (startDateTime && endDateTime && endDateTime <= startDateTime) {
        if (!startFromFull && !endFromFull) {
          endDateTime = new Date(endDateTime.getTime() + DAY_MS);
          warnings.push("End time moved to the next day.");
        } else {
          issues.push("End time must be after start time");
        }
      }

      return {
        row,
        bandKey,
        stageKey,
        bandStatus,
        stageStatus,
        bandMatches,
        stageMatches,
        issues,
        warnings,
        dateParts: dateParts ?? undefined,
        startTimeParts: startTimeParts ?? undefined,
        endTimeParts: endTimeParts ?? undefined,
        startDateTime,
        endDateTime,
      };
    });
  }, [rows, bandMatchMap, stageMatchMap, selectedEditionId, editionYear]);

  const bandDescriptionMap = useMemo(() => {
    const map = new Map<string, string>();

    rows.forEach((row) => {
      const key = normaliseName(row.artistName);
      if (!key || map.has(key)) return;

      const description = sanitiseCell(row.copyRaw);
      if (description) {
        map.set(key, description);
      }
    });

    return map;
  }, [rows]);

  const bandsReady = rows.length > 0 && rowDetails.every((row) => row.bandStatus === "ready");
  const stagesReady = rows.length > 0 && rowDetails.every((row) => row.stageStatus === "ready");
  const bookingsReady = rows.length > 0 && rowDetails.every((row) => row.issues.length === 0);

  const dataImportAnnouncement = useMemo(() => {
    if (importAnnouncementId) {
      return { id: importAnnouncementId };
    }

    const announcements = announcementsData?.announcements ?? [];
    return (
      announcements.find(
        (announcement) =>
          normaliseName(announcement.name) === normaliseName("Data Import")
      ) ?? null
    );
  }, [announcementsData, importAnnouncementId]);

  const uniqueStageRows = useMemo(() => {
    const map = new Map<string, RowDetails>();

    rowDetails.forEach((details) => {
      if (!details.stageKey) return;
      if (!map.has(details.stageKey)) {
        map.set(details.stageKey, details);
      }
    });

    return Array.from(map.values());
  }, [rowDetails]);

  const handleReset = () => {
    setRows([]);
    setFileName(undefined);
    setParseError(null);
    setHeaderPreview([]);
    setRowWarnings([]);
    setRawRowCount(0);
    setBandCreateStatuses({});
    setBandCreateErrors({});
    setStageCreateStatuses({});
    setStageCreateErrors({});
    setBookingStatuses({});
    setBookingErrors({});
    setSubmissionCounts(null);
    setIsSubmitting(false);
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    setFileName(file.name);
    setParseError(null);
    setRowWarnings([]);
    setRows([]);
    setHeaderPreview([]);
    setRawRowCount(0);

    Papa.parse<string[]>(file, {
      skipEmptyLines: true,
      complete: (result: ParseResult<string[]>) => {
        const { data, errors } = result;

        if (errors.length > 0) {
          const firstError: ParseError = errors[0];
          setParseError(
            `Unable to parse CSV (Row ${firstError.row}): ${firstError.message}`
          );
          return;
        }

        const tableRows = data.filter((row: any) => Array.isArray(row)) as string[][];

        if (tableRows.length === 0) {
          setParseError("The CSV file is empty.");
          return;
        }

        const rawHeaderRow = tableRows[0] ?? [];
        const headerRow = rawHeaderRow.map((value) => sanitiseCell(value));
        setHeaderPreview(headerRow);

        const { headerIndex, warnings, missingRequired } =
          resolveHeaderMap(headerRow);

        if (missingRequired.length > 0) {
          setParseError(
            `Missing required columns: ${missingRequired
              .map((key) => HEADER_LABELS[key])
              .join(", ")}`
          );
          return;
        }

        const dataRows = tableRows.slice(1);
        setRawRowCount(dataRows.length);

        const { rows: parsedRows, warnings: rowBuildWarnings } = buildRows(
          dataRows,
          headerIndex
        );
        setRows(parsedRows);
        setRowWarnings([...warnings, ...rowBuildWarnings]);

        if (parsedRows.length === 0) {
          setParseError(
            "No data rows were found after the header. Please check the CSV content."
          );
        }
      },
      error: (error: any) => {
        setParseError(`Failed to read the CSV file: ${error.message}`);
      },
    });

    event.target.value = "";
  };

  const handleCreateBand = async (name: string) => {
    const key = normaliseName(name);
    if (!key || bandMatchMap.get(key)?.length) {
      return;
    }

    if (!organisationId) {
      toast.error("Organisation ID is missing. Set REACT_APP_ORG_ID to continue.");
      return;
    }

    if (bandCreateStatuses[key] === "loading") {
      return;
    }

    setBandCreateStatuses((prev) => ({ ...prev, [key]: "loading" }));
    setBandCreateErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    try {
      const result = await createBandMutation({
        variables: {
          name,
          description: bandDescriptionMap.get(key) ?? "",
          organisationId,
        },
      });

      const createdId = result.data?.createBand?.id;
      if (createdId) {
        setCreatedBands((prev) => [...prev, { id: createdId, name }]);
        toast.success(`Created band: ${name}`);
        setBandCreateStatuses((prev) => ({ ...prev, [key]: "success" }));
      } else {
        throw new Error("No band id returned");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setBandCreateStatuses((prev) => ({ ...prev, [key]: "error" }));
      setBandCreateErrors((prev) => ({ ...prev, [key]: message }));
      toast.error(`Failed to create band ${name}.`);
    }
  };

  const handleCreateStage = async (name: string) => {
    const key = normaliseName(name);
    if (!key || stageMatchMap.get(key)?.length) {
      return;
    }

    if (stageCreateStatuses[key] === "loading") {
      return;
    }

    setStageCreateStatuses((prev) => ({ ...prev, [key]: "loading" }));
    setStageCreateErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

    try {
      const result = await createStageMutation({
        variables: {
          name,
        },
      });

      const createdId = result.data?.createStage?.id;
      if (createdId) {
        setCreatedStages((prev) => [...prev, { id: createdId, name }]);
        toast.success(`Created stage: ${name}`);
        setStageCreateStatuses((prev) => ({ ...prev, [key]: "success" }));
      } else {
        throw new Error("No stage id returned");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setStageCreateStatuses((prev) => ({ ...prev, [key]: "error" }));
      setStageCreateErrors((prev) => ({ ...prev, [key]: message }));
      toast.error(`Failed to create stage ${name}.`);
    }
  };

  const handleCreateBookings = async () => {
    if (
      !bookingsReady ||
      isSubmitting ||
      isDeletingAnnouncements ||
      !selectedEditionId ||
      announcementsLoading
    ) {
      return;
    }

    setBookingStatuses({});
    setBookingErrors({});
    setSubmissionCounts(null);
    setIsSubmitting(true);

    let successCount = 0;
    let failureCount = 0;
    let aborted = false;
    let announcementId = dataImportAnnouncement?.id ?? null;

    try {
      if (!announcementId) {
        try {
          const result = await createAnnouncementMutation({
            variables: {
              name: "Data Import",
              editionId: selectedEditionId,
              availabilityDate: null,
            },
            context: { skipAutoExportMessage: true },
          });

          announcementId = result.data?.createUpdateAnnouncement?.id ?? null;

          if (!announcementId) {
            throw new Error("No announcement id returned");
          }

          setImportAnnouncementId(announcementId);
          toast.success('Created announcement "Data Import".');
        } catch (error) {
          const message = error instanceof Error ? error.message : "Unknown error";
          toast.error(`Failed to create announcement: ${message}`);
          aborted = true;
          return;
        }
      }

      for (let index = 0; index < rowDetails.length; index += 1) {
        const details = rowDetails[index];

        if (details.issues.length > 0) {
          failureCount += 1;
          setBookingStatuses((prev) => ({ ...prev, [index]: "error" }));
          setBookingErrors((prev) => ({
            ...prev,
            [index]: details.issues.join("; "),
          }));
          continue;
        }

        const bandId = details.bandMatches[0]?.id;
        const stageId = details.stageMatches[0]?.id;
        const dateParts = details.dateParts;
        const startDateTime = details.startDateTime;
        const endDateTime = details.endDateTime;

        if (!bandId || !stageId || !dateParts || !startDateTime || !endDateTime) {
          failureCount += 1;
          setBookingStatuses((prev) => ({ ...prev, [index]: "error" }));
          setBookingErrors((prev) => ({
            ...prev,
            [index]: "Row is missing required booking details.",
          }));
          continue;
        }

        setBookingStatuses((prev) => ({ ...prev, [index]: "loading" }));

        try {
          await createBookingMutation({
            variables: {
              bandId,
              stageId,
              date: buildUtcDate(dateParts).toISOString(),
              startTime: startDateTime.toISOString(),
              endTime: endDateTime.toISOString(),
              announcementId,
              editionId: null,
            },
            context: { skipAutoExportMessage: true },
          });

          successCount += 1;
          setBookingStatuses((prev) => ({ ...prev, [index]: "success" }));
          setBookingErrors((prev) => {
            const next = { ...prev };
            delete next[index];
            return next;
          });
        } catch (error) {
          failureCount += 1;
          const message = error instanceof Error ? error.message : "Unknown error";
          setBookingStatuses((prev) => ({ ...prev, [index]: "error" }));
          setBookingErrors((prev) => ({ ...prev, [index]: message }));
        }
      }
    } finally {
      setIsSubmitting(false);

      if (aborted) {
        return;
      }

      setSubmissionCounts({ success: successCount, failure: failureCount });

      if (rows.length > 0) {
        if (failureCount === 0) {
          toast.success(`Submitted ${successCount} booking(s) successfully.`);
        } else {
          toast.error(
            `Submitted ${successCount + failureCount} booking(s): ${failureCount} failed.`
          );
        }
      }
    }
  };

  const handleDeleteEditionAnnouncements = () => {
    if (!selectedEditionId) {
      toast.error("Select a festival edition before deleting announcements.");
      return;
    }

    if (isDeletingAnnouncements || announcementsLoading) {
      return;
    }

    const editionId = selectedEditionId;
    const announcements = announcementsData?.announcements ?? [];
    const dataImportAnnouncements = announcements.filter(
      (announcement) =>
        normaliseName(announcement.name) === normaliseName("Data Import")
    );
    if (dataImportAnnouncements.length === 0) {
      toast.info('No "Data Import" announcement found for this edition.');
      return;
    }

    const editionLabel = selectedEdition
      ? `${selectedEdition.name}${selectedEdition.year ? ` (${selectedEdition.year})` : ""}`
      : "this edition";

    confirmAlert({
      title: "Delete all announcements?",
      message: `This will delete ${dataImportAnnouncements.length} "Data Import" announcement(s) and all of their bookings for ${editionLabel}. Are you sure? this can't be undone.`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            void deleteEditionAnnouncements(editionId, dataImportAnnouncements);
          },
        },
        {
          label: "No",
          onClick: () => {},
        },
      ],
    });
  };

  const deleteEditionAnnouncements = async (
    editionId: string,
    announcements: NonNullable<typeof announcementsData>["announcements"]
  ) => {
    if (!editionId) {
      return;
    }

    if (announcements.length === 0) {
      toast.info('No "Data Import" announcement found for this edition.');
      return;
    }

    setIsDeletingAnnouncements(true);
    setImportAnnouncementId(null);

    const bookingIds = new Set<string>();
    announcements.forEach((announcement) => {
      announcement.bands?.forEach((booking) => {
        if (booking?.id) {
          bookingIds.add(booking.id);
        }
      });
    });

    let bookingFailures = 0;
    let announcementFailures = 0;
    let lastError: string | null = null;

    try {
      for (const bookingId of Array.from(bookingIds)) {
        try {
          await deleteBookingMutation({
            variables: { bookingId },
            context: { skipAutoExportMessage: true },
          });
        } catch (error) {
          bookingFailures += 1;
          lastError = error instanceof Error ? error.message : "Unknown error";
        }
      }

      for (const announcement of announcements) {
        try {
          await deleteAnnouncementMutation({
            variables: { announcementId: announcement.id },
            context: { skipAutoExportMessage: true },
          });
        } catch (error) {
          announcementFailures += 1;
          lastError = error instanceof Error ? error.message : "Unknown error";
        }
      }

      if (selectedEditionId === editionId) {
        await refetchAnnouncements();
      }

      if (bookingFailures === 0 && announcementFailures === 0) {
        toast.success(
          `Deleted ${announcements.length} announcement(s) and ${bookingIds.size} booking(s).`
        );
      } else {
        toast.error(
          `Deleted with errors. Announcements failed: ${announcementFailures}, bookings failed: ${bookingFailures}. ${lastError ?? ""}`
        );
      }
    } finally {
      setIsDeletingAnnouncements(false);
    }
  };

  const isLookupLoading =
    bandsLoading || stagesLoading || editionsLoading || announcementsLoading;

  return (
    <Stack spacing={3}>
      <Typography variant="body1">
        Upload a booking CSV with flexible headers (case-insensitive). Required
        columns: Confirmed Artist (artist/band), Venue (stage), Start Time, End
        Time. Date is optional if start/end include full dates. The importer will
        check bands first, then stages, then create bookings with UTC dates/times.
      </Typography>
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        <Button variant="contained" component="label">
          Choose CSV
          <input
            hidden
            type="file"
            accept=".csv,text/csv"
            onChange={handleFileChange}
          />
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateBookings}
          disabled={
            !selectedEditionId ||
            !bookingsReady ||
            isSubmitting ||
            isDeletingAnnouncements ||
            announcementsLoading
          }
          startIcon={
            isSubmitting ? <CircularProgress color="inherit" size={16} /> : undefined
          }
        >
          {isSubmitting ? "Submitting..." : "Create bookings"}
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={handleDeleteEditionAnnouncements}
          disabled={!selectedEditionId || isDeletingAnnouncements || announcementsLoading}
          startIcon={
            isDeletingAnnouncements ? (
              <CircularProgress color="inherit" size={16} />
            ) : undefined
          }
        >
          {isDeletingAnnouncements ? "Deleting..." : "Delete previous import"}
        </Button>
        {rows.length > 0 || parseError ? (
          <Button variant="outlined" onClick={handleReset}>
            Clear
          </Button>
        ) : null}
      </Box>
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="booking-edition-select">Festival Edition</InputLabel>
              <Select
                labelId="booking-edition-select"
                label="Festival Edition"
                value={selectedEditionId}
                onChange={(event) => setSelectedEditionId(event.target.value)}
              >
                {editions.map((edition) => (
                  <MenuItem key={edition.id} value={edition.id}>
                    {edition.name} ({edition.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isLookupLoading && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading bands, stages, editions, and announcements...
                </Typography>
              </Box>
            )}
            </Stack>
          </CardContent>
        </Card>
      <Alert severity="info">
        Bookings will be attached to the "Data Import" announcement (created if missing).
      </Alert>
      {fileName && (
        <Typography variant="body2" color="text.secondary">
          Selected file: {fileName}
        </Typography>
      )}
      {parseError && <Alert severity="error">{parseError}</Alert>}
      {rowWarnings.length > 0 && !parseError && (
        <Alert severity="warning">
          <Stack spacing={0.5}>
            {rowWarnings.map((warning, index) => (
              <Typography key={`${warning}-${index}`} variant="body2">
                {warning}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
      {headerPreview.length > 0 && !parseError && (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2">Detected header</Typography>
            <Typography variant="body2" color="text.secondary">
              {headerPreview.join(", ")}
            </Typography>
          </CardContent>
        </Card>
      )}
      {rows.length > 0 && (
        <>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2">Parsed rows</Typography>
              <Typography variant="body2" color="text.secondary">
                {rows.length} row(s) ready for review
                {rawRowCount !== rows.length
                  ? ` (${rawRowCount - rows.length} empty rows skipped)`
                  : ""}
                .
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1">Step 1: Bands</Typography>
              <Typography variant="body2" color="text.secondary">
                Resolve any missing bands before moving on to stages.
              </Typography>
            </CardContent>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Artist</TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ width: 200 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowDetails.map((details, index) => {
                    const createStatus = bandCreateStatuses[details.bandKey];
                    const createError = bandCreateErrors[details.bandKey];
                    const statusMessage =
                      details.bandStatus === "duplicate"
                        ? "Multiple bands match this name"
                        : details.bandStatus === "missing"
                        ? "Band not found"
                        : undefined;

                    return (
                      <TableRow key={`${details.row.artistName}-${index}`}>
                        <TableCell>{details.row.artistName}</TableCell>
                        <TableCell align="center" sx={{ width: 120 }}>
                          {renderLookupStatus(details.bandStatus, statusMessage)}
                        </TableCell>
                        <TableCell sx={{ width: 200 }}>
                          {details.bandStatus === "missing" ? (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleCreateBand(details.row.artistName)}
                                disabled={createStatus === "loading"}
                              >
                                Create band
                              </Button>
                              {renderStatusIndicator(createStatus, createError)}
                            </Stack>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1">Step 2: Stages</Typography>
              {!bandsReady ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Resolve missing bands before creating stages.
                </Alert>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Resolve any missing stages before creating bookings.
                </Typography>
              )}
            </CardContent>
            {bandsReady && (
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                  <TableRow>
                    <TableCell>Venue</TableCell>
                    <TableCell align="center" sx={{ width: 120 }}>
                      Status
                    </TableCell>
                    <TableCell sx={{ width: 200 }}>Action</TableCell>
                  </TableRow>
                </TableHead>
                  <TableBody>
                    {uniqueStageRows.map((details) => {
                      const createStatus = stageCreateStatuses[details.stageKey];
                      const createError = stageCreateErrors[details.stageKey];
                      const statusMessage =
                        details.stageStatus === "duplicate"
                          ? "Multiple stages match this name"
                          : details.stageStatus === "missing"
                          ? "Stage not found"
                          : undefined;

                      return (
                        <TableRow key={details.stageKey || details.row.venueName}>
                          <TableCell>{details.row.venueName}</TableCell>
                          <TableCell align="center" sx={{ width: 120 }}>
                            {renderLookupStatus(details.stageStatus, statusMessage)}
                          </TableCell>
                          <TableCell sx={{ width: 200 }}>
                            {details.stageStatus === "missing" ? (
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleCreateStage(details.row.venueName)}
                                  disabled={createStatus === "loading"}
                                >
                                  Create stage
                                </Button>
                                {renderStatusIndicator(createStatus, createError)}
                              </Stack>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Stack spacing={2}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Typography variant="subtitle1">Step 3: Create bookings</Typography>
                  <Button
                    variant="contained"
                    onClick={handleCreateBookings}
                    disabled={
                      !selectedEditionId ||
                      !bookingsReady ||
                      isSubmitting ||
                      isDeletingAnnouncements ||
                      announcementsLoading
                    }
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress color="inherit" size={16} />
                      ) : undefined
                    }
                  >
                    {isSubmitting ? "Submitting..." : "Create bookings"}
                  </Button>
                </Box>
                {!bandsReady || !stagesReady ? (
                  <Alert severity="info">
                    Resolve missing bands and stages before creating bookings.
                  </Alert>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    All rows must be valid before creating bookings.
                  </Typography>
                )}
              </Stack>
            </CardContent>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {PREVIEW_HEADERS.map((label) => (
                      <TableCell key={label}>{label}</TableCell>
                    ))}
                    <TableCell align="center" sx={{ width: 120 }}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowDetails.map((details, index) => {
                    const status = bookingStatuses[index];
                    const errorMessage = bookingErrors[index];
                    const hasIssues = details.issues.length > 0;
                    const warningMessage = details.warnings.join(" ");
                    const issueMessage = details.issues.join("; ");

                    let statusIndicator = null;

                    if (hasIssues) {
                      statusIndicator = renderLookupStatus(
                        "missing",
                        issueMessage || "Row is not ready"
                      );
                    } else if (status) {
                      statusIndicator = renderStatusIndicator(status, errorMessage);
                    } else {
                      statusIndicator = renderLookupStatus(
                        "ready",
                        warningMessage || undefined
                      );
                    }

                    return (
                      <TableRow key={`${details.row.artistName}-${index}`}>
                        <TableCell>{details.row.artistName}</TableCell>
                        <TableCell>{details.row.venueName}</TableCell>
                        <TableCell>{details.row.dateRaw}</TableCell>
                        <TableCell>{details.row.startTimeRaw}</TableCell>
                        <TableCell>{details.row.endTimeRaw}</TableCell>
                        <TableCell align="center" sx={{ width: 120 }}>
                          {statusIndicator}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {submissionCounts && (
              <Alert
                severity={submissionCounts.failure > 0 ? "warning" : "success"}
                sx={{ mt: 2 }}
              >
                Submitted {rows.length} row(s). Success: {submissionCounts.success}
                . Failure: {submissionCounts.failure}.
              </Alert>
            )}
          </Card>
        </>
      )}
    </Stack>
  );
};

const BookingCsvImportPage = () => {
  return (
    <PageContainer title="Booking CSV Import">
      <BookingCsvImportContent />
    </PageContainer>
  );
};

export default BookingCsvImportPage;
